import { computed, ComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { UserRole } from '@/dashboard/types/user_role';
import { getProjectVisibilityType } from '@/dashboard/types/project_visibility';
import TeamUser from '@/dashboard/types/teamUser';
import { openEditProject } from '@/dashboard/utils/project/openEditProject';
import { Step } from '@/dashboard/types/step';
import { useAppStore } from '@/dashboard/store/appStore';
import { OwnerAdminCreator, OwnerAdminCreatorViewerClientRoles, OwnerAdminCreatorViewerRoles } from '@/dashboard/constants/roles';

export function usePublicTitlePanel() {
  const appStore = useAppStore();
  const route = useRoute();

  const { project, currentProjectUser } = storeToRefs(appStore);
  const canShowMenu: ComputedRef<boolean> = computed(() => {
    if (project.value?.temporary) {
      return false;
    } else if (currentProjectUser.value) {
      return OwnerAdminCreatorViewerClientRoles.includes(currentProjectUser.value?.role);
    } else {
      return false;
    }
  });
  const canEdit: ComputedRef<boolean> = computed(() => {
    const hasRole = currentProjectUser.value?.role ? OwnerAdminCreator.includes(currentProjectUser.value.role) : false;
    return !!project.value && !project.value.temporary && hasRole;
  });
  const canAddUsers = computed(() => canEdit.value && getProjectVisibilityType(project.value) !== 'private');
  const team: ComputedRef<TeamUser[]> = computed(() => {
    const users = project.value?.users ? project.value.users : [];
    return users.map((item) => new TeamUser(item.email, item.role, 'project', item.image, true));
  });
  const shortLink: ComputedRef<string | undefined> = computed(() => route.params?.short_link as string);
  const isSharedPanel = computed(() => {
    return !currentProjectUser.value || !project.value?.users?.some((user) => user.id === currentProjectUser.value?.id);
  });
  const owner = computed(() => project.value?.users?.find((user) => user.role === UserRole.Owner));

  async function addUser() {
    await openEditProject(shortLink.value ?? '', Step.Team);
  }

  return {
    project,
    canAddUsers,
    canShowMenu,
    team,
    isSharedPanel,
    owner,
    addUser,
  };
}
